/**
 * WorkflowRouteContainer.tsx — Route branch 容器行
 *
 * 职责：selected/running 分支可展开；skipped ◌ dim 静态一行。
 *
 * 链路位置：WorkflowChrome → scope branch 节点。
 */
import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';

import {
  buildStageDoneSummary,
  formatTeaserExpandHint,
} from '../../core/workflow/stageDoneSummary';
import { useSessionViewOptions } from '../../core/context/SessionContext';
import type { SessionTree } from '../../types/tree';
import type { WorkflowContainerRuntimeNode } from '../../types/workflowContainer';
import { AgentLoopView } from '../agentLoop/AgentLoopView';
import { WorkflowContainerLine } from './WorkflowContainerLine';
import { WorkflowExpandTrigger } from './WorkflowExpandTrigger';
import { WorkflowStageDoneBody } from './workflowStageDoneBody';
import {
  buildRouteLineMeta,
  mapContainerStatusLabel,
  mapContainerUiStatus,
  workflowIndentUnit,
} from './workflowContainerLineUtils';

export interface WorkflowRouteContainerProps {
  node: WorkflowContainerRuntimeNode;
  loopTree?: SessionTree;
  depth?: number;
  virtualized?: boolean;
  virtualizeThreshold?: number;
  groupParallelTools?: boolean;
}

export function WorkflowRouteContainer({
  node,
  loopTree,
  depth = 1,
  virtualized,
  virtualizeThreshold,
  groupParallelTools,
}: WorkflowRouteContainerProps) {
  const { verbose } = useSessionViewOptions();
  const isSkipped = node.status === 'skipped';
  const uiStatus = mapContainerUiStatus(node.status);
  const isRunning = !isSkipped && (node.status === 'running' || node.status === 'retrying');
  const isDone = !isSkipped && (node.status === 'completed' || node.status === 'failed');
  const testId = `lax-workflow-route-${node.scope_key}`;
  const doneSummary = useMemo(
    () => (loopTree && isDone ? buildStageDoneSummary(loopTree) : null),
    [loopTree, isDone],
  );
  const staticContentTeaser =
    !loopTree && node.content_blocks.length > 0
      ? node.content_blocks[node.content_blocks.length - 1]!.preview
      : '';
  const teaserText = doneSummary?.teaser || staticContentTeaser;
  const expandHint =
    doneSummary ? formatTeaserExpandHint(doneSummary.extraLines, verbose) : null;
  const doneLabel = doneSummary?.label ?? 'done';

  const [expanded, setExpanded] = useState(isRunning);

  useEffect(() => {
    if (isSkipped) return;
    if (isRunning) setExpanded(true);
    if (isDone) setExpanded(false);
  }, [isRunning, isDone, isSkipped]);

  const toggleExpanded = useCallback(() => {
    if (!isSkipped) setExpanded((prev) => !prev);
  }, [isSkipped]);

  const shellToggle =
    isSkipped ? undefined
    : isRunning ? toggleExpanded
    : isDone && expanded ? toggleExpanded
      : undefined;

  const depthStyle = {
    '--lax-workflow-depth': depth,
    '--lax-workflow-indent-unit': workflowIndentUnit(depth),
  } as CSSProperties;

  return (
    <div
      className={`lax-workflow-route-branch lax-workflow-stage-container lax-workflow-stage-container--${uiStatus}${
        isSkipped ? ' lax-workflow-route-branch--skipped' : ''
      }`}
      data-testid={testId}
      data-route-status={node.status}
      data-container-scope="branch"
      style={depthStyle}
    >
      <WorkflowContainerLine
        title={node.title}
        description={node.subtitle || undefined}
        uiStatus={uiStatus}
        meta={buildRouteLineMeta(node)}
        statusLabel={isSkipped ? 'skipped' : mapContainerStatusLabel(node.status)}
        onToggle={shellToggle}
        interactive={!isSkipped && isRunning}
        expanded={expanded}
        titleTestId={`${testId}-title`}
        descriptionTestId={`${testId}-desc`}
      />

      {!isSkipped && expanded && isRunning && loopTree ? (
        <div className="lax-workflow-stage-container__body" data-testid={`${testId}-body`}>
          <AgentLoopView
            tree={loopTree}
            showTaskListFooter={false}
            virtualized={virtualized}
            virtualizeThreshold={virtualizeThreshold}
            groupParallelTools={groupParallelTools}
          />
        </div>
      ) : null}

      {!isSkipped && isDone && !expanded ? (
        <WorkflowExpandTrigger
          summary={`${node.title} · ${doneLabel}`}
          teaser={teaserText || undefined}
          expandHint={expandHint}
          onToggle={() => setExpanded(true)}
          expanded={false}
          testId={`${testId}-done`}
          teaserTestId={teaserText ? `${testId}-teaser` : undefined}
        />
      ) : null}

      {!isSkipped && isDone && expanded ? (
        <WorkflowStageDoneBody
          node={node}
          loopTree={loopTree}
          testId={`${testId}-body`}
          virtualized={virtualized}
          virtualizeThreshold={virtualizeThreshold}
          groupParallelTools={groupParallelTools}
        />
      ) : null}
    </div>
  );
}
